import React from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { API, Auth, Storage, graphqlOperation } from 'aws-amplify';

import * as mutations from '../../graphql/mutations';
import * as subscriptions from '../../graphql/subscriptions';

import ChatService from '../../services/chat';
import UserService from '../../services/user';
import EnvironmentService from '../../services/environment';
import UnreadMessagesService from '../../services/unread-messages';

import Navigation from '../../components/navigation';
import Footer from '../../components/footer';
import ResponsiveNavigation from '../../components/responsive-navigation';
import HelmetComponent from '../../components/helmet';
import LoadingIndicator from '../../components/loading-indicator';

const chatAttachmentsBucket = () => EnvironmentService.chatAttachmentsBucket();
const chatAttachmentsUrlPrefix = () => `https://${chatAttachmentsBucket()}.s3.us-east-1.amazonaws.com/`;

const COGNITO_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// How many messages to pull per page. The conversation opens on the most recent page and
// walks backwards as the user scrolls up. The API caps a page at 25.
const MESSAGE_PAGE_SIZE = 20;
// How close to the top of the list (in px) the user has to scroll before we fetch the
// next page. Generous enough to start loading before they hit the very top.
const LOAD_OLDER_THRESHOLD_PX = 150;

// The API hands back the newest messages first; the list renders oldest-first.
const toAscending = messages => [...messages].sort((a, b) =>
    (a.sentAt || a.createdAt || '').localeCompare(b.sentAt || b.createdAt || ''));

const messageKey = m => `${m.sentAt || m.createdAt || ''}|${m.senderSub || ''}`;

// Reads the {messages, paging:{nextKey}} envelope, tolerating a bare array from older
// deployments of the chat API.
const readConversationPage = response => {
    const raw = Array.isArray(response?.data?.messages) ? response.data.messages
        : Array.isArray(response?.data) ? response.data : [];
    return { messages: toAscending(raw), nextKey: response?.data?.paging?.nextKey || null };
};
function looksLikeSubId(s) {
    return typeof s === 'string' && (COGNITO_UUID_RE.test(s) || s.startsWith('us-east-1:'));
}

// The inbox API returns subs only, so every conversation needs a name lookup. The user
// lambda runs under a small reserved-concurrency cap, so firing one request per
// conversation at once gets a chunk of them throttled - which is what surfaced as
// "Unknown user". Resolve them a few at a time, retry the throttles, and remember the
// answers for the rest of the session so a reload doesn't repeat the whole burst.
const publicNameCache = new Map();
// The user lambda reserves as few as 3 concurrent executions, and that budget is shared
// with every other caller, so keep two in flight to leave headroom and lean on retries.
const NAME_LOOKUP_CONCURRENCY = 2;
const NAME_LOOKUP_ATTEMPTS = 5;

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// Throttles and gateway errors are worth another go; a 404 is a real answer.
const isRetryableLookupError = status =>
    status === undefined || status === 429 || (status >= 500 && status <= 599);

async function fetchPublicName(sub) {
    for (let attempt = 1; attempt <= NAME_LOOKUP_ATTEMPTS; attempt++) {
        try {
            const res = await UserService.fetchByCognitoUsername(sub);
            return res?.data?.publicName || null;
        } catch (e) {
            const status = e?.response?.status;
            if (!isRetryableLookupError(status) || attempt === NAME_LOOKUP_ATTEMPTS) return null;
            // Back off with jitter so the retries don't all land together.
            await delay(300 * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 200));
        }
    }
    return null;
}

// Runs `worker` over `items` with at most `limit` in flight at any moment.
async function mapWithConcurrency(items, limit, worker) {
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (cursor < items.length) {
            const index = cursor++;
            await worker(items[index]);
        }
    });
    await Promise.all(runners);
}
// When a conversation last saw activity, as a comparable number. Copes with the ISO
// strings the chat API returns and with epoch numbers, and yields 0 when there's nothing
// usable so those sink to the bottom instead of shuffling around between renders.
function conversationTimestamp(c) {
    const raw = c?.createdAt || c?.sentAt || c?.lastMessageAt || null;
    if (!raw) return 0;
    if (typeof raw === 'number') return raw < 1e12 ? raw * 1000 : raw; // seconds vs millis
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
}

// Newest conversation first. A conversation the user just opened from a profile link has
// no messages yet, so pin it to the top rather than letting it fall to the bottom.
function byMostRecentActivity(a, b) {
    if (!!a.isNew !== !!b.isNew) return a.isNew ? -1 : 1;
    return conversationTimestamp(b) - conversationTimestamp(a);
}

function conversationDisplayName(c) {
    if (!c) return 'Unknown user';
    const candidates = [c.publicName, c.otherUserPublicName, c.name];
    for (const cand of candidates) {
        if (cand && !looksLikeSubId(cand)) return cand;
    }
    return 'Unknown user';
}

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : null;

function getBlurredAttachmentUrl(url) {
    const prefix = chatAttachmentsUrlPrefix();
    if (!url || !url.startsWith(prefix)) return null;
    const key = url.slice(prefix.length);
    // key format: conversations/{a}/{b}/{filename}
    // blur Lambda writes to: conversations/{b}/{a}/blurred/{filename}
    const rest = key.startsWith('conversations/') ? key.slice('conversations/'.length) : null;
    if (!rest) return null;
    const slashA = rest.indexOf('/');
    if (slashA === -1) return null;
    const a = rest.slice(0, slashA);
    const afterA = rest.slice(slashA + 1);
    const slashB = afterA.indexOf('/');
    if (slashB === -1) return null;
    const b = afterA.slice(0, slashB);
    const filename = afterA.slice(slashB + 1);
    return `${prefix}conversations/${b}/${a}/blurred/${filename}`;
}

// Passed per call rather than via Storage.configure: configure is global, and Storage.get
// reads it only after awaiting credentials, so concurrent calls would pick up the wrong bucket.
function chatAttachmentsStorageOptions(extra) {
    return { bucket: chatAttachmentsBucket(), region: 'us-east-1', customPrefix: { public: '' }, level: 'public', ...extra };
}

// SigV4 requires RFC 3986 encoding; encodeURIComponent leaves !'()* alone, which breaks the
// signature for names like "IMG_1234 (1).jpg".
function encodeS3KeySegment(segment) {
    return encodeURIComponent(segment).replace(/[!'()*]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}

async function tagS3Object(key, tags) {
    const credentials = await Auth.currentCredentials();
    const { accessKeyId, secretAccessKey, sessionToken } = credentials;
    const bucket = chatAttachmentsBucket();
    const region = 'us-east-1';
    const host = `${bucket}.s3.${region}.amazonaws.com`;
    const encodedKey = key.split('/').map(encodeS3KeySegment).join('/');

    const taggingXml = `<Tagging><TagSet>${
        Object.entries(tags).map(([k, v]) => `<Tag><Key>${k}</Key><Value>${v}</Value></Tag>`).join('')
    }</TagSet></Tagging>`;

    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, '').slice(0, 15) + 'Z';
    const dateStamp = amzDate.slice(0, 8);

    const bodyEncoder = new TextEncoder();
    const bodyBytes = bodyEncoder.encode(taggingXml);
    const bodyHashBuf = await crypto.subtle.digest('SHA-256', bodyBytes);
    const bodyHash = Array.from(new Uint8Array(bodyHashBuf)).map(b => b.toString(16).padStart(2, '0')).join('');

    const headerMap = {
        host,
        'content-type': 'application/xml',
        'x-amz-date': amzDate,
        'x-amz-content-sha256': bodyHash,
        ...(sessionToken ? { 'x-amz-security-token': sessionToken } : {}),
    };
    const sortedKeys = Object.keys(headerMap).sort();
    const canonicalHeaders = sortedKeys.map(k => `${k}:${headerMap[k]}`).join('\n') + '\n';
    const signedHeaders = sortedKeys.join(';');

    const canonicalRequest = ['PUT', `/${encodedKey}`, 'tagging=', canonicalHeaders, signedHeaders, bodyHash].join('\n');

    const credScope = `${dateStamp}/${region}/s3/aws4_request`;

    const enc = new TextEncoder();
    const importKey = async (raw) => crypto.subtle.importKey('raw', raw, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const hmac = async (key, msg) => new Uint8Array(await crypto.subtle.sign('HMAC', await importKey(key), enc.encode(msg)));

    const crHashBuf = await crypto.subtle.digest('SHA-256', enc.encode(canonicalRequest));
    const crHash = Array.from(new Uint8Array(crHashBuf)).map(b => b.toString(16).padStart(2, '0')).join('');
    const stringToSign = `AWS4-HMAC-SHA256\n${amzDate}\n${credScope}\n${crHash}`;

    const signingKey = await hmac(
        await hmac(await hmac(await hmac(enc.encode('AWS4' + secretAccessKey), dateStamp), region), 's3'),
        'aws4_request'
    );
    const sig = Array.from(new Uint8Array(await crypto.subtle.sign('HMAC', await importKey(signingKey), enc.encode(stringToSign))))
        .map(b => b.toString(16).padStart(2, '0')).join('');

    const authHeader = `AWS4-HMAC-SHA256 Credential=${accessKeyId}/${credScope}, SignedHeaders=${signedHeaders}, Signature=${sig}`;

    const res = await fetch(`https://${host}/${encodedKey}?tagging`, {
        method: 'PUT',
        headers: { ...headerMap, Authorization: authHeader },
        body: taggingXml,
    });
    if (!res.ok) throw new Error(`tagS3Object ${res.status}: ${await res.text()}`);
}

async function getPresignedUrl(url) {
    const prefix = chatAttachmentsUrlPrefix();
    if (!url || !url.startsWith(prefix)) return url;
    const key = url.slice(prefix.length);
    return Storage.get(key, chatAttachmentsStorageOptions());
}

class AttachmentMedia extends React.Component {
    constructor(props) {
        super(props);
        const needsPresign = props.url && props.url.startsWith(chatAttachmentsUrlPrefix());
        this.state = { src: needsPresign ? null : props.url, retries: 0 };
        this._retryTimeout = null;
    }

    componentDidMount() {
        this._presign(this.props.url);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.url !== this.props.url) {
            this._cancelRetry();
            this.setState({ retries: 0 });
            this._presign(this.props.url);
        }
    }

    componentWillUnmount() {
        this._cancelRetry();
    }

    _cancelRetry() {
        if (this._retryTimeout) {
            clearTimeout(this._retryTimeout);
            this._retryTimeout = null;
        }
    }

    _presign(url) {
        if (url && url.startsWith(chatAttachmentsUrlPrefix())) {
            this.setState({ src: null });
            getPresignedUrl(url).then(src => {
                if (src) this.setState({ src });
            }).catch(() => {});
        } else {
            this.setState({ src: url });
        }
    }

    _handleImageError() {
        const { retries } = this.state;
        const url = this.props.url;
        const isBlurred = url && url.includes('/blurred/');
        if (isBlurred && retries < 4) {
            this.setState({ src: null, retries: retries + 1 });
            this._retryTimeout = setTimeout(() => this._presign(url), 5000);
        }
    }

    render() {
        const { type, name, onOpen, onLoad } = this.props;
        const { src } = this.state;
        if (!src) {
            return <div className="chat-bubble-media-placeholder" />;
        }
        return type === 'video'
            ? <video src={src} controls preload="metadata" className="chat-bubble-media"
                     onLoadedData={() => onLoad && onLoad()} />
            : <img src={src} alt={name || 'attachment'} className="chat-bubble-media chat-bubble-media--clickable"
                   onClick={() => onOpen && onOpen(src)}
                   onLoad={() => onLoad && onLoad()}
                   onError={() => this._handleImageError()} />;
    }
}

function decodeMessagePayload(messageInBase64) {
    try {
        const raw = decodeURIComponent(escape(atob(messageInBase64 || '')));
        const parsed = JSON.parse(raw);
        return {
            text: parsed.message || '',
            attachments: Array.isArray(parsed.attachments) ? parsed.attachments : [],
            price: parsed.price || null,
        };
    } catch {
        try {
            return { text: decodeURIComponent(escape(atob(messageInBase64 || ''))), attachments: [] };
        } catch {
            return { text: messageInBase64 || '', attachments: [] };
        }
    }
}

class Chat extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            conversations: [],
            selectedConversation: null,
            messages: [],
            isLoadingInbox: true,
            isLoadingMessages: false,
            isLoadingOlderMessages: false,
            olderMessagesKey: null,
            newMessage: '',
            mobileView: 'list',
            search: '',
            activeFilter: null,
            attachments: [],
            showPricePopup: false,
            priceInput: '',
            messagePrice: null,
            currentUserSub: null,
            followerSubs: null,
            deletePendingConversation: null,
            swipedConversationKey: null,
            lightboxSrc: null,
            purchaseModal: null,
            purchaseLoading: false,
            purchaseError: null,
            currentUserCard: null,
            isSending: false,
            // Client-side messaging gate for the open conversation:
            // 'ok' | 'blocked_by_me' | 'blocked_me' | 'must_follow'.
            messagingStatus: 'ok',
            blockedSubs: new Set(),      // subs I have blocked
            blockedBySubs: new Set(),    // subs who have blocked me
            recipientMeta: null,         // { _id, fom } for the open conversation (for the Follow button)
        };

        // sub -> { _id, fom }; profile rarely changes, so cache it. Follow state is NOT
        // cached here — it is re-read each check so a fresh Follow unlocks immediately.
        this._recipientMetaCache = {};

        this.subscription = null;
        this.tickInterval = null;
        this._touchStartX = null;
        this._uploadPromises = {};
        this.fileInputRef = React.createRef();
        this.messagesEndRef = React.createRef();
        this.messagesListRef = React.createRef();
        this._userScrolledUp = false;
        // Set just before older messages are prepended, so componentDidUpdate can keep the
        // viewport anchored instead of snapping to the newest message.
        this._prependAnchor = null;
        // Background name resolution can outlive the component; don't setState after that.
        this._unmounted = false;

        this.handleSelectConversation = this.handleSelectConversation.bind(this);
        this.loadOlderMessages = this.loadOlderMessages.bind(this);
        this._handleMessagesScroll = this._handleMessagesScroll.bind(this);
        this._handleAttachmentLoad = this._handleAttachmentLoad.bind(this);
        this.handleMessageChange = this.handleMessageChange.bind(this);
        this.handleSendMessage = this.handleSendMessage.bind(this);
        this.handleBackToList = this.handleBackToList.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleFilterClick = this.handleFilterClick.bind(this);
        this.handleAttachmentChange = this.handleAttachmentChange.bind(this);
        this.handleRemoveAttachment = this.handleRemoveAttachment.bind(this);
        this.handlePriceConfirm = this.handlePriceConfirm.bind(this);
        this.handlePriceClear = this.handlePriceClear.bind(this);
        this.handleInboundMessage = this.handleInboundMessage.bind(this);
        this.handleDeleteConversation = this.handleDeleteConversation.bind(this);
        this.handleUnlockClick = this.handleUnlockClick.bind(this);
        this.handlePurchaseConfirm = this.handlePurchaseConfirm.bind(this);
        this.handleBlockUser = this.handleBlockUser.bind(this);
        this.handleUnblockUser = this.handleUnblockUser.bind(this);
        this.handleFollowFromGate = this.handleFollowFromGate.bind(this);
    }

    async componentDidMount() {
        document.body.classList.add('body--chat');
        let username;
        try {
            username = await Auth.currentSession().then(s => s.getIdToken().decodePayload()['cognito:username']);
            this.setState({ currentUserSub: username });

            UserService.fetchCurrent().then(res => {
                const data = res?.data;
                if (data?.card_last4) {
                    this.setState({ currentUserCard: { last4: data.card_last4, brand: data.card_brand, paymentMethodId: data.stripe_payment_method_id } });
                }
            }).catch(() => {});

            this.subscription = API.graphql(
                graphqlOperation(subscriptions.listenToInbox, { inboxOwner: username })
            ).subscribe({
                next: ({ provider, value }) => {
                    console.log('Chat received:', { provider, value });
                    const msg = value?.data?.listenToInbox;
                    if (msg) this.handleInboundMessage(msg);
                },
                error: (err) => console.warn('Chat subscription error:', err),
            });

            console.log('Chat subscription started, owner:', username);
        } catch (e) {
            console.warn('Chat auth error:', e);
        }

        this.tickInterval = setInterval(() => this.setState({ now: Date.now() }), 30000);

        this.loadBlocks();
        await this.loadInbox(username);
    }

    async loadBlocks() {
        try {
            const res = await UserService.fetchBlocks();
            if (this._unmounted) return;
            this.setState({
                blockedSubs: new Set(res?.data?.blocked || []),
                blockedBySubs: new Set(res?.data?.blockedBy || []),
            }, () => {
                if (this.state.selectedConversation) this.refreshMessagingStatus(this.state.selectedConversation);
            });
        } catch (e) {
            console.warn('Failed to load block lists:', e);
        }
    }

    // Resolves the client-side messaging gate for a conversation and stores it in state.
    // Block checks are synchronous (already in state); the follow-gate needs the recipient's
    // profile (fom flag + whether I follow them). On any failure it defaults to 'ok' so a
    // network hiccup never locks someone out.
    async refreshMessagingStatus(conversation) {
        const sub = conversation && conversation.recipientSub;
        if (!sub) {
            this.setState({ messagingStatus: 'ok', recipientMeta: null });
            return;
        }
        if (this.state.blockedSubs.has(sub)) {
            this.setState({ messagingStatus: 'blocked_by_me', recipientMeta: null });
            return;
        }
        if (this.state.blockedBySubs.has(sub)) {
            this.setState({ messagingStatus: 'blocked_me', recipientMeta: null });
            return;
        }

        let meta = this._recipientMetaCache[sub];
        try {
            if (!meta) {
                const profile = (await UserService.fetchByCognitoUsername(sub))?.data;
                meta = { _id: profile?._id || null, fom: !!profile?.followers_only_messaging };
                this._recipientMetaCache[sub] = meta;
            }
            let following = true;
            if (meta.fom && meta._id) {
                const f = await UserService.isFollowing(meta._id);
                following = (f?.data?.following?.length || 0) > 0;
            }
            // The user may have switched conversations while this was in flight.
            if (this._unmounted || this.state.selectedConversation?.recipientSub !== sub) return;
            this.setState({ messagingStatus: (meta.fom && !following) ? 'must_follow' : 'ok', recipientMeta: meta });
        } catch (e) {
            console.warn('Failed to resolve messaging status for', sub, e);
            if (this._unmounted || this.state.selectedConversation?.recipientSub !== sub) return;
            this.setState({ messagingStatus: 'ok', recipientMeta: meta || null });
        }
    }

    async handleBlockUser(conversation) {
        const sub = conversation && conversation.recipientSub;
        if (!sub) return;
        // Optimistic: lock the UI immediately, revert if the request fails.
        this.setState(prev => {
            const s = new Set(prev.blockedSubs); s.add(sub); return { blockedSubs: s };
        }, () => this.refreshMessagingStatus(this.state.selectedConversation));
        try {
            await UserService.blockUser(sub);
        } catch (e) {
            console.warn('Failed to block user:', e);
            this.setState(prev => {
                const s = new Set(prev.blockedSubs); s.delete(sub); return { blockedSubs: s };
            }, () => this.refreshMessagingStatus(this.state.selectedConversation));
        }
    }

    async handleUnblockUser(conversation) {
        const sub = conversation && conversation.recipientSub;
        if (!sub) return;
        this.setState(prev => {
            const s = new Set(prev.blockedSubs); s.delete(sub); return { blockedSubs: s };
        }, () => this.refreshMessagingStatus(this.state.selectedConversation));
        try {
            await UserService.unblockUser(sub);
        } catch (e) {
            console.warn('Failed to unblock user:', e);
            this.setState(prev => {
                const s = new Set(prev.blockedSubs); s.add(sub); return { blockedSubs: s };
            }, () => this.refreshMessagingStatus(this.state.selectedConversation));
        }
    }

    async handleFollowFromGate() {
        const meta = this.state.recipientMeta;
        if (!meta || !meta._id) return;
        try {
            await UserService.follow(meta._id);
            await this.refreshMessagingStatus(this.state.selectedConversation);
        } catch (e) {
            console.warn('Failed to follow from gate:', e);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const messagesChanged = prevState.messages !== this.state.messages;

        if (messagesChanged) {
            const el = this.messagesListRef.current;
            if (el && this._prependAnchor) {
                // Older messages were just prepended. Push the viewport down by exactly the
                // height they added, so whatever the user was reading stays under the
                // cursor instead of the list snapping to the newest message. Applied to the
                // live scrollTop so it stays correct if they kept scrolling while it loaded.
                el.scrollTop += el.scrollHeight - this._prependAnchor.scrollHeight;
                this._prependAnchor = null;
            } else {
                if (el) el.scrollTop = el.scrollHeight;
                this._userScrolledUp = false;
            }
        }

        if (messagesChanged || prevState.isLoadingOlderMessages !== this.state.isLoadingOlderMessages) {
            this._ensureMessagesFillPanel();
        }

        // Any change to the conversation list can change the unread total (load,
        // mark-read, inbound message, delete). Push it from here so the nav badge stays
        // in sync no matter which path touched it.
        if (prevState.conversations !== this.state.conversations) {
            this._syncUnreadBadge();
        }
    }

    _syncUnreadBadge() {
        const total = this.state.conversations.reduce((sum, c) => sum + (c.unread || 0), 0);
        UnreadMessagesService.setCount(total);
    }

    // A page that doesn't fill the panel leaves nothing to scroll, so the user would have
    // no way to reach the top and ask for the next one. Pull pages in until the list
    // overflows or the history runs out.
    _ensureMessagesFillPanel() {
        const el = this.messagesListRef.current;
        if (!el) return;
        const { isLoadingMessages, isLoadingOlderMessages, olderMessagesKey } = this.state;
        if (isLoadingMessages || isLoadingOlderMessages || !olderMessagesKey) return;
        if (el.scrollHeight <= el.clientHeight) this.loadOlderMessages();
    }

    _handleMessagesScroll() {
        const el = this.messagesListRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < 100) {
            this._userScrolledUp = false;
        } else if (distanceFromBottom > 400) {
            this._userScrolledUp = true;
        }
        if (el.scrollTop <= LOAD_OLDER_THRESHOLD_PX) {
            this.loadOlderMessages();
        }
    }

    _handleAttachmentLoad() {
        if (this._scrollScheduled) return;
        this._scrollScheduled = true;
        requestAnimationFrame(() => {
            this._scrollScheduled = false;
            if (!this._userScrolledUp && this.messagesListRef.current) {
                const el = this.messagesListRef.current;
                el.scrollTop = el.scrollHeight;
            }
        });
    }

    componentWillUnmount() {
        this._unmounted = true;
        document.body.classList.remove('body--chat');
        if (this.subscription) this.subscription.unsubscribe();
        if (this.tickInterval) clearInterval(this.tickInterval);
    }

    async loadInbox(currentUserSub) {
        currentUserSub = currentUserSub || this.state.currentUserSub;
        try {
            const response = await ChatService.listInbox();
            const data = response?.data;
            let conversations;

            const isValidSub = sub => sub && !sub.startsWith('us-east-1:');
            const otherSub = item => {
                if (currentUserSub && item.recipientSub === currentUserSub && item.senderSub) {
                    return item.senderSub;
                }
                return item.recipientSub;
            };
            const pickName = item => {
                for (const cand of [item.name, item.otherUserPublicName, item.publicName]) {
                    if (cand && !looksLikeSubId(cand)) return cand;
                }
                return null;
            };
            const normalize = items => items
                .filter(item => isValidSub(item.recipientSub) || isValidSub(item.senderSub))
                .map(item => {
                    const other = otherSub(item);
                    return {
                        ...item,
                        recipientSub: other,
                        inboxId: item.inboxId || other,
                        name: pickName(item),
                    };
                });

            if (Array.isArray(data?.conversations)) {
                conversations = normalize(data.conversations);
            } else if (Array.isArray(data?.inbox)) {
                conversations = normalize(data.inbox);
            } else if (Array.isArray(data?.messages)) {
                conversations = this.groupMessagesToConversations(data.messages, currentUserSub);
            } else if (Array.isArray(data)) {
                conversations = normalize(data);
            } else {
                conversations = [];
            }

            // Show the list straight away with whatever names we already know. Waiting on
            // the lookups here is what made the inbox look like it never loaded.
            const withKnownNames = conversations.map(c => {
                const cached = publicNameCache.get(c.recipientSub);
                return cached ? { ...c, publicName: cached, name: cached, otherUserPublicName: cached } : c;
            });

            this.setState({ conversations: withKnownNames, isLoadingInbox: false }, () => {
                this.openConversationFromUrl();
            });

            this.resolveConversationNames(withKnownNames)
                .catch(e => console.warn('Failed to resolve some conversation names:', e));
        } catch (e) {
            console.warn('Failed to load the chat inbox:', e);
            this.setState({ isLoadingInbox: false }, () => this.openConversationFromUrl());
        }
    }

    // Fills in display names in the background, patching each one into the list as it
    // arrives so the inbox is usable immediately instead of waiting on the whole batch.
    async resolveConversationNames(conversations) {
        const subs = [...new Set(
            conversations
                .filter(c => c.recipientSub && (looksLikeSubId(c.publicName) || !c.publicName || looksLikeSubId(c.name)))
                .map(c => c.recipientSub)
        )].filter(sub => !publicNameCache.has(sub));

        if (!subs.length) return;

        const resolveOne = async sub => {
            const publicName = await fetchPublicName(sub);
            if (!publicName || this._unmounted) return;

            publicNameCache.set(sub, publicName);

            const applyName = c => (c && c.recipientSub === sub)
                ? { ...c, publicName, name: publicName, otherUserPublicName: publicName }
                : c;

            this.setState(prev => {
                const next = { conversations: prev.conversations.map(applyName) };
                // Keep the open conversation's header in step if its name lands late.
                if (prev.selectedConversation?.recipientSub === sub) {
                    next.selectedConversation = applyName(prev.selectedConversation);
                }
                return next;
            });
        };

        await mapWithConcurrency(subs, NAME_LOOKUP_CONCURRENCY, resolveOne);

        // Anything still missing was most likely squeezed out by throttling. Sweep the
        // stragglers one at a time rather than leaving them showing "Unknown user".
        const stragglers = subs.filter(sub => !publicNameCache.has(sub));
        if (stragglers.length && !this._unmounted) {
            await mapWithConcurrency(stragglers, 1, resolveOne);
        }
    }

    groupMessagesToConversations(messages, currentUserSub) {
        const convMap = new Map();

        for (const msg of messages) {
            const other = (currentUserSub && msg.recipientSub === currentUserSub && msg.senderSub)
                ? msg.senderSub
                : msg.recipientSub;
            if (!other || other.startsWith('us-east-1:')) continue;

            const { text, attachments } = decodeMessagePayload(msg.messageInBase64);
            const preview = text || (attachments.length ? '📎 Attachment' : '');

            if (!convMap.has(other)) {
                convMap.set(other, {
                    inboxId: other,
                    recipientSub: other,
                    otherUserSub: other,
                    publicName: null,
                    otherUserPublicName: null,
                    name: null,
                    lastMessage: preview,
                    lastMsg: preview,
                    createdAt: msg.sentAt,
                    unread: msg.status === 'UNREAD' ? 1 : 0,
                });
            } else {
                const conv = convMap.get(other);
                if (msg.status === 'UNREAD') conv.unread += 1;
                // The inbox comes back newest-first today, but don't bank on it: keep the
                // newest message as both the preview and the conversation's timestamp.
                if (conversationTimestamp({ createdAt: msg.sentAt }) > conversationTimestamp(conv)) {
                    conv.lastMessage = preview;
                    conv.lastMsg = preview;
                    conv.createdAt = msg.sentAt;
                }
            }
        }

        return [...convMap.values()];
    }

    async openConversationFromUrl() {
        const targetPublicName = this.getPublicNameFromUrl();
        if (!targetPublicName) return;

        let userData;
        try {
            const userResponse = await UserService.fetch(targetPublicName);
            userData = userResponse?.data;
        } catch (e) {
            console.warn('Failed to load user for conversation:', e);
            return;
        }

        const cognitoUsername = userData?.cognitoUsername;

        // Seed the recipient meta cache from the profile we already fetched (it carries
        // _id + the followers-only flag) so the gate resolves without a second request.
        if (cognitoUsername) {
            this._recipientMetaCache[cognitoUsername] = {
                _id: userData?._id || null,
                fom: !!userData?.followers_only_messaging,
            };
        }

        // Check if inbox already has a conversation with this user
        const existing = this.state.conversations.find(c => c.recipientSub === cognitoUsername);
        if (existing) {
            this.setState({ selectedConversation: existing, mobileView: 'messages' });
            this.handleSelectConversation(existing);
            return;
        }

        // No existing conversation — add a new entry. The follow-gate (if the recipient
        // requires it) is surfaced in the composer by refreshMessagingStatus, replacing the
        // old behaviour of silently bouncing the user back to the profile page.
        const enriched = {
            inboxId: null,
            publicName: userData?.publicName || targetPublicName,
            otherUserPublicName: userData?.publicName || targetPublicName,
            name: userData?.publicName || targetPublicName,
            recipientSub: cognitoUsername,
            lastMessage: '',
            createdAt: '',
            unread: 0,
            isNew: true,
        };
        this.setState(prev => ({
            conversations: [enriched, ...prev.conversations.filter(c => !c.isNew)],
            selectedConversation: enriched,
            mobileView: 'messages',
            messagingStatus: 'ok',
            recipientMeta: null,
        }), () => this.refreshMessagingStatus(enriched));
    }

    getPublicNameFromUrl() {
        const parts = this.props.location.pathname.split('/');
        return parts[2] || null;
    }

    isAuthenticated() {
        return !!this.props.currentUser;
    }

    async handleSelectConversation(conversation) {
        // Reset the gate optimistically to 'ok'; refreshMessagingStatus resolves the real
        // status (block checks are instant, the follow-gate resolves a moment later).
        this.setState({
            selectedConversation: conversation,
            messages: [],
            isLoadingMessages: true,
            isLoadingOlderMessages: false,
            olderMessagesKey: null,
            mobileView: 'messages',
            messagingStatus: 'ok',
            recipientMeta: null,
        });
        this._prependAnchor = null;
        this.refreshMessagingStatus(conversation);

        if (!conversation.recipientSub) {
            this.setState({ isLoadingMessages: false });
            return;
        }

        try {
            const response = await ChatService.listConversation(conversation.recipientSub, { limit: MESSAGE_PAGE_SIZE });
            const { messages, nextKey } = readConversationPage(response);
            // Guard against a slow response for a conversation the user already left.
            if (this.state.selectedConversation?.recipientSub !== conversation.recipientSub) return;
            this.setState({ messages, olderMessagesKey: nextKey, isLoadingMessages: false });
        } catch (e) {
            this.setState({ isLoadingMessages: false });
        }

        // Mark as read
        this.setState(prev => ({
            conversations: prev.conversations.map(c =>
                c.inboxId === conversation.inboxId ? { ...c, unread: 0 } : c
            ),
        }));
    }

    async loadOlderMessages() {
        const { selectedConversation, olderMessagesKey, isLoadingOlderMessages, isLoadingMessages } = this.state;
        if (!selectedConversation || !olderMessagesKey || isLoadingOlderMessages || isLoadingMessages) return;

        const conversationSub = selectedConversation.recipientSub;
        const listEl = this.messagesListRef.current;
        // Captured before the spinner mounts, so that by the time it has unmounted again
        // the growth in scrollHeight is exactly the height the older messages added.
        const heightBeforeLoad = listEl ? listEl.scrollHeight : null;

        this.setState({ isLoadingOlderMessages: true });

        try {
            const response = await ChatService.listConversation(conversationSub, {
                limit: MESSAGE_PAGE_SIZE,
                nextKey: olderMessagesKey,
            });
            const { messages: older, nextKey } = readConversationPage(response);

            // The user may have switched conversations while this was in flight.
            if (this.state.selectedConversation?.recipientSub !== conversationSub) {
                this.setState({ isLoadingOlderMessages: false });
                return;
            }

            // A page can overlap what's already on screen, so drop anything we hold. If
            // that leaves nothing, there's no prepend and the scroll must not be touched.
            const known = new Set(this.state.messages.map(messageKey));
            const fresh = older.filter(m => !known.has(messageKey(m)));

            if (!fresh.length) {
                this.setState({ olderMessagesKey: nextKey, isLoadingOlderMessages: false });
                return;
            }

            this._prependAnchor = heightBeforeLoad === null ? null : { scrollHeight: heightBeforeLoad };
            this.setState(prev => {
                const seen = new Set(prev.messages.map(messageKey));
                return {
                    messages: [...fresh.filter(m => !seen.has(messageKey(m))), ...prev.messages],
                    olderMessagesKey: nextKey,
                    isLoadingOlderMessages: false,
                };
            });
        } catch (e) {
            this._prependAnchor = null;
            this.setState({ isLoadingOlderMessages: false });
        }
    }

    handleInboundMessage(msg) {
        const { selectedConversation, conversations } = this.state;
        const otherSub = msg.senderSub;

        // Suppress messages from someone I've blocked (client-side mutual block).
        if (otherSub && this.state.blockedSubs.has(otherSub)) return;

        const { text: inboundText, attachments: inboundAttachments } = decodeMessagePayload(msg.messageInBase64);
        const inboundPreview = inboundText || (inboundAttachments.length ? '📎 Attachment' : '');

        if (selectedConversation && selectedConversation.recipientSub === otherSub) {
            this.setState(prev => ({ messages: [...prev.messages, msg] }));
        }

        const exists = conversations.find(c => c.recipientSub === otherSub);
        if (exists) {
            this.setState(prev => ({
                conversations: prev.conversations.map(c =>
                    c.recipientSub === otherSub
                        ? { ...c, lastMessage: inboundPreview, lastMsg: inboundPreview, createdAt: msg.sentAt, unread: selectedConversation?.recipientSub === otherSub ? 0 : (c.unread || 0) + 1 }
                        : c
                ),
            }));
        } else {
            this.loadInbox();
        }
    }

    handleBackToList() {
        this.setState({ mobileView: 'list' });
    }

    handleSearchChange(event) {
        this.setState({ search: event.target.value });
    }

    handleFilterClick(filter) {
        const next = this.state.activeFilter === filter ? null : filter;
        this.setState({ activeFilter: next, followerSubs: null });
        if (next === 'Followers') {
            this.loadFollowers();
        }
    }

    handleAttachmentChange(event) {
        const files = Array.from(event.target.files);
        if (!files.length) return;
        const { currentUserSub, selectedConversation } = this.state;
        const recipientSub = selectedConversation?.recipientSub || selectedConversation?.otherUserSub;

        const newAttachments = files.map(file => {
            const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const attachment = {
                id,
                file,
                url: URL.createObjectURL(file),
                type: file.type.startsWith('video') ? 'video' : 'image',
                name: file.name,
                uploading: true,
                s3Url: null,
            };

            this._uploadPromises[id] = this.uploadChatAttachment(file, currentUserSub, recipientSub)
                .then(result => {
                    this.setState(prev => ({
                        attachments: prev.attachments.map(a =>
                            a.id === id ? { ...a, s3Url: result.url, uploading: false } : a
                        ),
                    }));
                    return result;
                })
                .catch(err => {
                    console.error('attachment upload failed:', err);
                    this.setState(prev => ({
                        attachments: prev.attachments.map(a =>
                            a.id === id ? { ...a, uploading: false, uploadError: true } : a
                        ),
                    }));
                    return null;
                });

            return attachment;
        });

        this.setState(prev => ({ attachments: [...prev.attachments, ...newAttachments] }));
        event.target.value = '';
    }

    handleRemoveAttachment(index) {
        this.setState(prev => {
            const removed = prev.attachments[index];
            if (removed) URL.revokeObjectURL(removed.url);
            return { attachments: prev.attachments.filter((_, i) => i !== index) };
        });
    }

    handlePriceConfirm() {
        const price = parseFloat(this.state.priceInput);
        if (!isNaN(price) && price > 0) {
            this.setState({ messagePrice: price, showPricePopup: false, priceInput: '' });
        }
    }

    handlePriceClear() {
        this.setState({ messagePrice: null, showPricePopup: false, priceInput: '' });
    }

    handleUnlockClick(message) {
        this.setState({ purchaseModal: message, purchaseError: null });
    }

    async handlePurchaseConfirm() {
        const { purchaseModal, currentUserCard } = this.state;
        if (!purchaseModal || !currentUserCard?.paymentMethodId) return;

        this.setState({ purchaseLoading: true, purchaseError: null });

        const senderSub = purchaseModal.senderSub;
        const sentAt = purchaseModal.sentAt;

        try {
            const result = await ChatService.purchaseChatMessage({ senderSub, sentAt });
            const data = result?.data;

            if (data?.requiresAction) {
                const stripe = stripePromise ? await stripePromise : null;
                if (!stripe) throw new Error('#StripeNotLoaded');
                const { paymentIntent, error } = await stripe.confirmCardPayment(data.clientSecret);
                if (error) {
                    this.setState({ purchaseError: error.message, purchaseLoading: false });
                    return;
                }
                if (paymentIntent?.status === 'succeeded') {
                    await ChatService.confirmChatMessagePurchase({ paymentIntentId: paymentIntent.id, senderSub, sentAt });
                }
            }

            this.setState(prev => ({
                messages: prev.messages.map(m => {
                    const mSentAt = m.sentAt || m.createdAt;
                    if (m.senderSub === senderSub && mSentAt === sentAt) {
                        return { ...m, isPurchased: true };
                    }
                    return m;
                }),
                purchaseModal: null,
                purchaseLoading: false,
            }));
        } catch (e) {
            const errCode = e?.response?.data?.error || '#PurchaseFailed';
            this.setState({ purchaseError: errCode, purchaseLoading: false });
        }
    }

    handleMessageChange(event) {
        this.setState({ newMessage: event.target.value });
    }

    handleKeyDown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.handleSendMessage();
        }
    }

    async uploadChatAttachment(file, senderSub, recipientSub) {
        // The file picker can be used before componentDidMount has resolved the session.
        if (!senderSub) {
            senderSub = await Auth.currentSession().then(s => s.getIdToken().decodePayload()['cognito:username']);
        }
        if (!recipientSub) throw new Error('uploadChatAttachment: recipientSub is missing');
        // Keep the key to URL/SigV4-safe characters; the original name is kept in `name`.
        const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
        const key = `conversations/${senderSub}/${recipientSub}/${Date.now()}-${safeName}`;
        // Unlike get, Amplify's put doesn't refresh credentials: it reuses whatever the last
        // Storage.get cached, which expire after ~1h. undefined makes it fetch fresh ones.
        await Storage.put(key, file, chatAttachmentsStorageOptions({ contentType: file.type || undefined, credentials: undefined }));
        return {
            key,
            url: `${chatAttachmentsUrlPrefix()}${key}`,
            type: file.type.startsWith('video') ? 'video' : 'image',
            name: file.name,
        };
    }

    async handleSendMessage() {
        const { newMessage, selectedConversation, attachments, messagePrice, currentUserSub } = this.state;
        if (!newMessage.trim() && attachments.length === 0) return;
        if (!selectedConversation) return;
        // Client-side guard: block/follow-gate. The composer is already hidden in these
        // states, but stop here too in case a send is triggered another way.
        if (this.state.messagingStatus !== 'ok') return;

        const recipientSub = selectedConversation.recipientSub || selectedConversation.otherUserSub;
        if (!recipientSub) {
            console.warn('sendChatMessage: recipientSub is null, conversation:', selectedConversation);
            return;
        }

        this.setState({ isSending: true });

        // Await any still-in-progress uploads (started on attach)
        const uploadedAttachments = (await Promise.all(
            attachments.map(a => this._uploadPromises[a.id] || Promise.resolve(a.s3Url ? { url: a.s3Url, type: a.type, name: a.name } : null))
        )).filter(Boolean);
        attachments.forEach(a => delete this._uploadPromises[a.id]);

        if (messagePrice && uploadedAttachments.length > 0) {
            await Promise.all(
                uploadedAttachments
                    .filter(a => a.key)
                    .map(a => tagS3Object(a.key, { BlurImage: 'true' }).catch(err => console.error('attachment tagging failed:', err)))
            );
        }

        if ((!messagePrice || messagePrice <= 0) && uploadedAttachments.length > 0) {
            await Promise.all(
                uploadedAttachments
                    .filter(a => a.key)
                    .map(a => tagS3Object(a.key, { canBeDownloadedBy: recipientSub }).catch(err => console.error('attachment tagging failed:', err)))
            );
        }

        const messagePayload = {}

        messagePayload["message"] = newMessage.trim();
        messagePayload["attachments"] = uploadedAttachments.map(a => ({ url: a.url, type: a.type, name: a.name }));
        messagePayload["type"] = "textWithAttachments"
        if (messagePrice) messagePayload["price"] = messagePrice;

        const messageInBase64 = btoa(unescape(encodeURIComponent(JSON.stringify(messagePayload))));
        const price = messagePrice || 0;
        const isPurchased = price ? false : null;

        API.graphql(graphqlOperation(mutations.sendChatMessage, { messageInBase64, recipientSub, price, isPurchased }))
            .catch(err => console.error('sendChatMessage error:', err));

        // Optimistic update — use local blob URLs so the image appears instantly
        const optimisticMsg = {
            id: `tmp-${Date.now()}`,
            s: messagePayload.message,
            senderSub: currentUserSub,
            createdAt: new Date().toISOString(),
            fromSelf: true,
            attachments: attachments.map(a => ({ url: a.url, type: a.type, name: a.name })),
            price: messagePrice,
        };

        this.setState(prev => ({
            messages: [...prev.messages, optimisticMsg],
            newMessage: '',
            attachments: [],
            messagePrice: null,
            isSending: false,
            conversations: prev.conversations.map(c =>
                c.inboxId === selectedConversation?.inboxId || c.isNew
                    ? { ...c, lastMessage: messagePayload.message, createdAt: new Date().toISOString() }
                    : c
            ),
        }));
    }

    async loadFollowers() {
        try {
            const response = await UserService.fetchFollowers();
            const items = response?.data?.followers || response?.data?.items || response?.data || [];
            const names = new Set(items.map(u => u.publicName).filter(Boolean));
            this.setState({ followerSubs: names });
        } catch (e) {
            this.setState({ followerSubs: new Set() });
        }
    }

    filteredConversations() {
        const { conversations, search, activeFilter, followerSubs } = this.state;
        // Sorting here rather than at each mutation site keeps the list correct no matter
        // which path last touched it: inbox load, an inbound message, or one just sent.
        // filter() already returns a copy, so this never mutates state.
        return conversations.filter(c => {
            if (search) {
                const q = search.toLowerCase();
                const name = [c.name, c.otherUserPublicName, c.publicName]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!name.includes(q)) return false;
            }
            if (activeFilter === 'Unread' && !c.unread) return false;
            if (activeFilter === 'Followers') {
                if (!followerSubs) return true;
                const convName = c.publicName || c.otherUserPublicName || c.name;
                if (!convName || !followerSubs.has(convName)) return false;
            }
            return true;
        }).sort(byMostRecentActivity);
    }

    formatTime(createdAt) {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${Math.floor(diffHours / 24)}d ago`;
    }

    renderNavigation() {
        return <Navigation isAuthenticated={this.isAuthenticated()} currentUser={this.props.currentUser} navigation={this.props.navigation} />;
    }

    renderResponsiveNavigation() {
        return <ResponsiveNavigation isAuthenticated={this.isAuthenticated()} currentUser={this.props.currentUser} navigation={this.props.navigation} />;
    }

    handleConversationTouchStart(e) {
        this._touchStartX = e.touches[0].clientX;
    }

    handleConversationTouchEnd(e, key) {
        if (this._touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - this._touchStartX;
        this._touchStartX = null;
        if (dx < -50) {
            this.setState({ swipedConversationKey: key });
        } else if (dx > 20) {
            this.setState({ swipedConversationKey: null });
        }
    }

    handleDeleteConversation(e, conversation) {
        e.stopPropagation();
        this.setState({ deletePendingConversation: conversation });
    }

    handleDeleteConfirm(e) {
        e.stopPropagation();
        const conversation = this.state.deletePendingConversation;
        if (!conversation) return;
        const recipientSub = conversation.recipientSub;
        this.setState({ deletePendingConversation: null });
        if (!recipientSub) return;
        API.graphql(graphqlOperation(mutations.deleteConversation, { recipientSub }))
            .catch(err => console.error('deleteConversation error:', err));
        this.setState(prev => {
            const conversations = prev.conversations.filter(c => c.recipientSub !== recipientSub);
            const selectedConversation = prev.selectedConversation?.recipientSub === recipientSub ? null : prev.selectedConversation;
            return { conversations, selectedConversation, messages: selectedConversation === null ? [] : prev.messages };
        });
    }

    handleDeleteCancel(e) {
        e.stopPropagation();
        this.setState({ deletePendingConversation: null });
    }

    renderConversationItem(conversation) {
        const isSelected = this.state.selectedConversation &&
            (this.state.selectedConversation.inboxId === conversation.inboxId ||
             (conversation.isNew && this.state.selectedConversation.isNew));
        const isPendingDelete = this.state.deletePendingConversation?.recipientSub === conversation.recipientSub;
        const name = conversationDisplayName(conversation);
        const lastMsg = conversation.lastMessage || conversation.lastMsg || '';
        const swipeKey = conversation.recipientSub || conversation.publicName;
        const isSwiped = this.state.swipedConversationKey === swipeKey;
        return (
            <div
                key={conversation.inboxId || conversation.publicName}
                className="chat-conversation-item-wrap"
            >
                <button
                    className="chat-swipe-delete-action"
                    onClick={e => this.handleDeleteConversation(e, conversation)}
                >
                    Delete
                </button>
                <div
                    className={`chat-conversation-item${isSelected ? ' chat-conversation-item--selected' : ''}${isSwiped ? ' chat-conversation-item--swiped' : ''}`}
                    onClick={() => {
                        if (isSwiped) { this.setState({ swipedConversationKey: null }); return; }
                        this.handleSelectConversation(conversation);
                    }}
                    onTouchStart={e => this.handleConversationTouchStart(e)}
                    onTouchEnd={e => this.handleConversationTouchEnd(e, swipeKey)}
                >
                    <div className="chat-conversation-avatar">
                        <span className="chat-conversation-avatar-letter">{name.charAt(0).toUpperCase()}</span>
                        <button
                            className="chat-conversation-avatar-delete"
                            onClick={e => this.handleDeleteConversation(e, conversation)}
                            title="Delete conversation"
                        >&#10005;</button>
                        {isPendingDelete && (
                            <div className="chat-delete-confirm-popup" onClick={e => e.stopPropagation()}>
                                <span className="chat-delete-confirm-text">Delete?</span>
                                <button className="chat-delete-confirm-yes" onClick={e => this.handleDeleteConfirm(e)}>Yes</button>
                                <button className="chat-delete-confirm-no" onClick={e => this.handleDeleteCancel(e)}>No</button>
                            </div>
                        )}
                    </div>
                    <div className="chat-conversation-info">
                        <div className="chat-conversation-header">
                            <span className="chat-conversation-name">{name}</span>
                            <span className="chat-conversation-time">{this.formatTime(conversation.createdAt)}</span>
                        </div>
                        <div className="chat-conversation-preview">
                            <span className="chat-conversation-last-message">{lastMsg}</span>
                            {conversation.unread > 0 && (
                                <span className="chat-unread-badge">{conversation.unread}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderConversationList() {
        const { search, activeFilter } = this.state;
        const filters = ['Unread', 'Followers'];
        return (
            <div className={`chat-panel chat-panel--left${this.state.mobileView === 'messages' ? ' chat-panel--hidden-mobile' : ''}`}>
                <div className="chat-panel-header">
                    <h4 className="chat-panel-title">Messages</h4>
                </div>
                <div className="chat-search-area">
                    <input
                        className="chat-search-input"
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={this.handleSearchChange}
                    />
                    <div className="chat-filters">
                        {filters.map(f => (
                            <button
                                key={f}
                                className={`chat-filter-btn${activeFilter === f ? ' chat-filter-btn--active' : ''}`}
                                onClick={() => this.handleFilterClick(f)}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="chat-conversation-list">
                    {this.filteredConversations().map(c => this.renderConversationItem(c))}
                </div>
            </div>
        );
    }

    renderMessageBubble(message) {
        const fromSelf = message.fromSelf || message.senderSub === this.state.currentUserSub;

        let text = message.text || message.s || '';
        let attachments = message.attachments || [];
        let price = message.price || null;
        let isPurchased = message.isPurchased || false;

        if (message.messageInBase64) {
            const decoded = decodeMessagePayload(message.messageInBase64);
            if (!text) text = decoded.text;
            if (!attachments.length) attachments = decoded.attachments;
            if (!price) price = decoded.price;
        }

        const isLocked = price && !fromSelf && !isPurchased;
        const time = message.timestamp || this.formatTime(message.sentAt || message.createdAt);

        return (
            <div key={message.id || message.sentAt || message.createdAt} className={`chat-message-row${fromSelf ? ' chat-message-row--self' : ''}`}>
                <div className={`chat-bubble${fromSelf ? ' chat-bubble--self' : ' chat-bubble--other'}`}>
                    {isLocked ? (
                        <div className="chat-bubble-locked">
                            {attachments.length > 0 && (
                                <div className="chat-bubble-locked-media">
                                    {attachments.map((a, i) => {
                                        if (a.type === 'video') {
                                            return (
                                                <div key={i} className="chat-bubble-attachment chat-bubble-attachment--locked">
                                                    <div className="chat-bubble-video-placeholder">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M8 5v14l11-7z"/>
                                                        </svg>
                                                    </div>
                                                    <div className="chat-bubble-lock-overlay" />
                                                </div>
                                            );
                                        }
                                        const blurredUrl = getBlurredAttachmentUrl(a.url);
                                        return (
                                            <div key={i} className="chat-bubble-attachment chat-bubble-attachment--locked">
                                                {blurredUrl && <AttachmentMedia url={blurredUrl} type={a.type} name={a.name} onLoad={this._handleAttachmentLoad} />}
                                                <div className="chat-bubble-lock-overlay" />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="chat-bubble-locked-info">
                                <span className="chat-bubble-locked-icon">&#128274;</span>
                                <span className="chat-bubble-locked-label">Pay ${price.toFixed(2)} to unlock</span>
                                <button className="chat-bubble-unlock-btn" onClick={() => this.handleUnlockClick(message)}>Unlock</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {attachments.length > 0 && (
                                <div className="chat-bubble-attachments">
                                    {attachments.map((a, i) => (
                                        <div key={i} className="chat-bubble-attachment">
                                            <AttachmentMedia url={a.url} type={a.type} name={a.name} onOpen={src => this.setState({ lightboxSrc: src })} onLoad={this._handleAttachmentLoad} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {price && fromSelf && (
                                isPurchased
                                    ? <div className="chat-bubble-price-tag chat-bubble-price-tag--sold"><span role="img" aria-label="sold">&#9989;</span> Sold · ${price.toFixed(2)}</div>
                                    : <div className="chat-bubble-price-tag"><span role="img" aria-label="locked">&#128274;</span> Locked · ${price.toFixed(2)}</div>
                            )}
                            {text && <span className="chat-bubble-text">{text}</span>}
                        </>
                    )}
                    <span className="chat-bubble-time">{time}</span>
                </div>
            </div>
        );
    }

    renderMessagesPanel() {
        const { selectedConversation, newMessage, mobileView } = this.state;

        if (!selectedConversation) {
            return (
                <div className={`chat-panel chat-panel--right chat-panel--empty${mobileView === 'list' ? ' chat-panel--hidden-mobile' : ''}`}>
                    <p className="chat-empty-state">Select a conversation to start chatting</p>
                </div>
            );
        }

        const name = conversationDisplayName(selectedConversation);
        const handleCandidate = selectedConversation.otherUserPublicName || selectedConversation.publicName || '';
        const handle = looksLikeSubId(handleCandidate) ? '' : handleCandidate;
        const isBlockedByMe = this.state.blockedSubs.has(selectedConversation.recipientSub);

        return (
            <div className={`chat-panel chat-panel--right${mobileView === 'list' ? ' chat-panel--hidden-mobile' : ''}`}>
                <div className="chat-panel-header">
                    <button className="chat-back-button d-block d-md-none" onClick={this.handleBackToList}>
                        &#8592; Back
                    </button>
                    <div className="chat-panel-contact" style={{cursor: 'pointer'}} onClick={() => handle && this.props.navigation.userPage(handle)}>
                        <div className="chat-panel-contact-avatar">
                            {name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div className="chat-panel-contact-name">{name}</div>
                            <div className="chat-panel-contact-handle">@{handle}</div>
                        </div>
                    </div>
                    {selectedConversation.recipientSub && (
                        <button
                            className="chat-block-button"
                            onClick={() => isBlockedByMe ? this.handleUnblockUser(selectedConversation) : this.handleBlockUser(selectedConversation)}
                            title={isBlockedByMe ? 'Unblock this user' : 'Block this user'}
                        >
                            {isBlockedByMe ? 'Unblock' : 'Block'}
                        </button>
                    )}
                </div>
                <div className="chat-messages-list" ref={this.messagesListRef} onScroll={this._handleMessagesScroll}>
                    {this.state.isLoadingMessages
                        ? <div className="chat-messages-loader"><div className="chat-messages-spinner" /></div>
                        : <>
                            {this.state.isLoadingOlderMessages && (
                                <div className="chat-messages-older-loader"><div className="chat-messages-spinner chat-messages-spinner--small" /></div>
                            )}
                            {this.state.messages.map(m => this.renderMessageBubble(m))}
                        </>
                    }
                    <div ref={this.messagesEndRef} />
                </div>
                {this.state.attachments.length > 0 && (
                    <div className="chat-attachment-preview-strip">
                        {this.state.attachments.map((a, i) => (
                            <div key={a.id || i} className={`chat-attachment-preview${a.uploading ? ' chat-attachment-preview--uploading' : ''}${a.uploadError ? ' chat-attachment-preview--error' : ''}`}>
                                {a.type === 'video'
                                    ? <video src={a.url} className="chat-attachment-preview-media" muted preload="metadata" onLoadedMetadata={e => { e.target.currentTime = 0.001; }} />
                                    : <img src={a.url} alt="preview" className="chat-attachment-preview-media" />
                                }
                                {a.uploading && <div className="chat-attachment-upload-spinner" />}
                                <button className="chat-attachment-remove" onClick={() => this.handleRemoveAttachment(i)}>&#x2715;</button>
                            </div>
                        ))}
                    </div>
                )}
                {this.state.messagingStatus !== 'ok' ? this.renderMessagingGate(name) : (
                <div className="chat-input-area">
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        className="chat-file-input"
                        onChange={this.handleAttachmentChange}
                    />
                    <button className="chat-attach-button" onClick={() => this.fileInputRef.current.click()} title="Attach image or video">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                        </svg>
                    </button>
                    <div className="chat-price-wrapper">
                        <button
                            className={`chat-attach-button${this.state.messagePrice ? ' chat-attach-button--active' : ''}`}
                            onClick={() => this.setState(prev => ({ showPricePopup: !prev.showPricePopup }))}
                            title="Set pay-to-open price"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                        </button>
                        {this.state.showPricePopup && (
                            <div className="chat-price-popup">
                                <div className="chat-price-popup-title">Set unlock price</div>
                                <div className="chat-price-popup-input-row">
                                    <span className="chat-price-popup-symbol">$</span>
                                    <input
                                        className="chat-price-popup-input"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={this.state.priceInput}
                                        onChange={e => this.setState({ priceInput: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && this.handlePriceConfirm()}
                                        autoFocus
                                    />
                                </div>
                                <div className="chat-price-popup-actions">
                                    <button className="chat-price-popup-cancel" onClick={this.handlePriceClear}>Remove</button>
                                    <button className="chat-price-popup-confirm" onClick={this.handlePriceConfirm}>Set</button>
                                </div>
                            </div>
                        )}
                    </div>
                    {this.state.messagePrice && (
                        <span className="chat-price-badge">${this.state.messagePrice.toFixed(2)}</span>
                    )}
                    <input
                        className="chat-input"
                        type="text"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={this.handleMessageChange}
                        onKeyDown={this.handleKeyDown}
                    />
                    <button className="chat-send-button" onClick={this.handleSendMessage} disabled={this.state.isSending}>
                        <span className="d-none d-md-inline">{this.state.isSending ? 'Sending…' : 'Send'}</span>
                        <svg className="d-inline d-md-none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 2L11 13"/>
                            <path d="M22 2L15 22 11 13 2 9l20-7z"/>
                        </svg>
                    </button>
                </div>
                )}
            </div>
        );
    }

    // Replaces the composer when the current user can't message this conversation.
    renderMessagingGate(name) {
        const status = this.state.messagingStatus;
        if (status === 'must_follow') {
            return (
                <div className="chat-gate-banner">
                    <div className="chat-gate-text">You must follow {name} to message them.</div>
                    <button className="chat-gate-action" onClick={this.handleFollowFromGate}>Follow</button>
                </div>
            );
        }
        if (status === 'blocked_by_me') {
            return (
                <div className="chat-gate-banner">
                    <div className="chat-gate-text">You blocked this user.</div>
                    <button className="chat-gate-action" onClick={() => this.handleUnblockUser(this.state.selectedConversation)}>Unblock</button>
                </div>
            );
        }
        if (status === 'blocked_me') {
            return (
                <div className="chat-gate-banner">
                    <div className="chat-gate-text">You can't message this user.</div>
                </div>
            );
        }
        return null;
    }

    renderContent() {
        if (this.state.isLoadingInbox) {
            return (
                <section className="featured">
                    <div className="container">
                        <div className="top-padding td-center">
                            <LoadingIndicator />
                        </div>
                    </div>
                </section>
            );
        }
        return (
            <section className="featured chat-page">
                <div className="container chat-container">
                    <div className="chat-layout">
                        {this.renderConversationList()}
                        {this.renderMessagesPanel()}
                    </div>
                </div>
            </section>
        );
    }

    renderNotAuthenticated() {
        return (
            <section className="featured">
                <div className="container">
                    <div className="top-padding td-center">
                        <p>Please <a href="/signin" onClick={(e) => { e.preventDefault(); this.props.navigation.signIn(); }}>sign in</a> to view your messages.</p>
                    </div>
                </div>
            </section>
        );
    }

    renderPurchaseModal() {
        const { purchaseModal, currentUserCard, purchaseLoading, purchaseError } = this.state;
        if (!purchaseModal) return null;
        const price = purchaseModal.price;
        return (
            <div className="chat-purchase-overlay" onClick={() => { if (!purchaseLoading) this.setState({ purchaseModal: null }); }}>
                <div className="chat-purchase-modal" onClick={e => e.stopPropagation()}>
                    <div className="chat-purchase-header">
                        <h4 className="chat-purchase-title">Unlock message</h4>
                        <button className="chat-purchase-close" disabled={purchaseLoading} onClick={() => this.setState({ purchaseModal: null })}>&#x2715;</button>
                    </div>
                    <div className="chat-purchase-body">
                        <div className="chat-purchase-amount">${price?.toFixed(2)}</div>
                        {currentUserCard ? (
                            <div className="chat-purchase-card">
                                <span className="chat-purchase-card-brand">{currentUserCard.brand?.toUpperCase()}</span>
                                <span className="chat-purchase-card-last4">•••• {currentUserCard.last4}</span>
                            </div>
                        ) : (
                            <p className="chat-purchase-no-card">No payment method saved. Add a card in <a href="/my/settings" onClick={e => { e.preventDefault(); this.props.navigation.settings(); }}>Settings</a>.</p>
                        )}
                        {purchaseError && <div className="chat-purchase-error">{purchaseError}</div>}
                    </div>
                    <div className="chat-purchase-actions">
                        <button className="chat-purchase-cancel" disabled={purchaseLoading} onClick={() => this.setState({ purchaseModal: null })}>Cancel</button>
                        <button className="chat-purchase-confirm" disabled={purchaseLoading || !currentUserCard} onClick={this.handlePurchaseConfirm}>
                            {purchaseLoading ? 'Processing…' : `Pay $${price?.toFixed(2)}`}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderDeleteModal() {
        const conversation = this.state.deletePendingConversation;
        if (!conversation) return null;
        const resolved = conversationDisplayName(conversation);
        const name = resolved === 'Unknown user' ? 'this conversation' : resolved;
        return (
            <div className="chat-delete-modal-overlay" onClick={e => this.handleDeleteCancel(e)}>
                <div className="chat-delete-modal" onClick={e => e.stopPropagation()}>
                    <h4 className="chat-delete-modal-title">Delete conversation?</h4>
                    <p className="chat-delete-modal-body">Remove your conversation with <strong>{name}</strong>? This cannot be undone.</p>
                    <div className="chat-delete-modal-actions">
                        <button className="chat-delete-modal-cancel" onClick={e => this.handleDeleteCancel(e)}>Cancel</button>
                        <button className="chat-delete-modal-confirm" onClick={e => this.handleDeleteConfirm(e)}>Delete</button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div>
                <header>
                    <div className="nav-container">
                        <div className="container">
                            {this.renderNavigation()}
                        </div>
                    </div>
                </header>

                {this.isAuthenticated() ? this.renderContent() : this.renderNotAuthenticated()}

                {this.renderDeleteModal()}
                {this.renderPurchaseModal()}
                {this.state.lightboxSrc && (
                    <div className="chat-lightbox" onClick={() => this.setState({ lightboxSrc: null })}>
                        <button className="chat-lightbox-close" onClick={() => this.setState({ lightboxSrc: null })}>&#x2715;</button>
                        <img className="chat-lightbox-img" src={this.state.lightboxSrc} alt="attachment" onClick={e => e.stopPropagation()} />
                    </div>
                )}
                <Footer />
                {this.renderResponsiveNavigation()}
                <HelmetComponent title="Chat" description="Your messages on OpenSwap." />
            </div>
        );
    }
}

export default Chat;
